import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Search,
  FileSpreadsheet,
  FileText,
  ChevronDown,
  TrendingUp,
  AlertCircle,
  BarChart3,
  ChevronRight,
  Users,
  Loader2,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Info,
  Check,
  Plus,
} from 'lucide-react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import 'antd/dist/reset.css';
import { useTheme } from '../context/ThemeContext';
import ExcelExportButton from '../components/ExcelExportButton';

// ── Import from your existing api.js ─────────────────────────────────────────
import { dashboardApi, authHelpers, fetchApiDirect } from '../services/api';

const { RangePicker } = DatePicker;

// ── Revenue-specific API calls (all use targetType=REVENUE) ──────────────────
const revenueApi = {
  getKPI: (startDate, endDate, extra = {}) => {
    const p = new URLSearchParams({ type: 'REVENUE' });
    if (startDate)     p.append('startDate', startDate);
    if (endDate)       p.append('endDate',   endDate);
    if (extra.process) p.append('process',   extra.process);
    if (extra.shift)   p.append('shift',     extra.shift);
    return fetchApiDirect(`/dashboard/business/kpi?${p}`);
  },

  getMonthlyPerformance: (startDate, endDate) => {
    const p = new URLSearchParams({ type: 'REVENUE' });
    if (startDate) p.append('startDate', startDate);
    if (endDate)   p.append('endDate',   endDate);
    return fetchApiDirect(`/dashboard/business/monthly-performance?${p}`);
  },

  getFunnel: (startDate, endDate) => {
    const p = new URLSearchParams({ type: 'REVENUE' });
    if (startDate) p.append('startDate', startDate);
    if (endDate)   p.append('endDate',   endDate);
    return fetchApiDirect(`/dashboard/business/funnel?${p}`);
  },

  getManagersFocus: (startDate, endDate) => {
    const p = new URLSearchParams({ type: 'REVENUE' });
    if (startDate) p.append('startDate', startDate);
    if (endDate)   p.append('endDate',   endDate);
    return fetchApiDirect(`/dashboard/managers/focus?${p}`);
  },

  getAllManagers: (startDate, endDate, page = 0, size = 20) => {
    const p = new URLSearchParams({ type: 'REVENUE', page, size });
    if (startDate) p.append('startDate', startDate);
    if (endDate)   p.append('endDate',   endDate);
    return fetchApiDirect(`/dashboard/managers/all?${p}`);
  },

  getTeamInsights: (managerId, startDate, endDate) => {
    const p = new URLSearchParams({ type: 'REVENUE' });
    if (startDate) p.append('startDate', startDate);
    if (endDate)   p.append('endDate',   endDate);
    return fetchApiDirect(`/dashboard/managers/${managerId}/team-insights?${p}`);
  },

  getTeamLeadAgents: (tlId, startDate, endDate, page = 0, size = 10) => {
    const p = new URLSearchParams({ type: 'REVENUE', page, size });
    if (startDate) p.append('startDate', startDate);
    if (endDate)   p.append('endDate',   endDate);
    return fetchApiDirect(`/dashboard/teamleads/${tlId}/agents?${p}`);
  },

  getAssignees: (assigneeType) =>
    fetchApiDirect(`/targets/assignees?targetType=REVENUE&assigneeType=${assigneeType}`),

  assignTarget: (body) =>
    fetchApiDirect(`/targets/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
};

// ── Format helpers ────────────────────────────────────────────────────────────
const fmtN   = (v) => { if (v == null) return '—'; const n = Number(v); return isNaN(n) ? String(v) : n.toLocaleString('en-IN'); };
const fmtPct = (v) => v != null ? `${Number(v).toFixed(1)}%` : '—';
const fmtRs  = (v) => v != null ? `${fmtN(v)}` : '—';

// ── Status config ─────────────────────────────────────────────────────────────
const statusConfig = (p) => {
  if (p >= 90) return { label: 'Good',            color: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0' };
  if (p >= 75) return { label: 'On track',        color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' };
               return { label: 'Low performance', color: '#f97316', bg: '#fff7ed', border: '#fed7aa' };
};

// ─────────────────────────────────────────────────────────────────────────────
const MISRevenueTarget = () => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const navigate  = useNavigate();
  const location  = useLocation();

  // ── state ──────────────────────────────────────────────────────────────────
 const [dates, setDates] = useState([
  dayjs().startOf('month'),
  dayjs().endOf('month'),
]);
  const [processFilter, setProcessFilter] = useState('');
  const [shiftFilter,   setShiftFilter]   = useState('');
  const [searchQuery,   setSearchQuery]   = useState('');

  const [kpi,          setKpi]          = useState(null);
  const [monthly,      setMonthly]      = useState(null);
  const [funnel,       setFunnel]       = useState(null);
  const [managers,     setManagers]     = useState([]);
  const [teamInsights, setTeamInsights] = useState(null);
  const [selectedMgr,  setSelectedMgr]  = useState(null);
  const [expandedLead, setExpandedLead] = useState(null);
  const [agentsByLead, setAgentsByLead] = useState({});

  const [loading, setLoading] = useState({
    kpi: false, monthly: false, funnel: false, managers: false, insights: false,
  });
  const [errors, setErrors] = useState({});

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignment, setAssignment] = useState({ type: 'TEAM_LEAD', target: '' });
  const [assignees,  setAssignees]  = useState([]);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState([]);
  const [assigneeTargets, setAssigneeTargets] = useState({});
  const [assigning,  setAssigning]  = useState(false);

  const [toast, setToast] = useState({ show: false, message: '', isError: false });

  // ── helpers ────────────────────────────────────────────────────────────────
  const startDate = dates?.[0]?.format('YYYY-MM-DD');
  const endDate   = dates?.[1]?.format('YYYY-MM-DD');

  const showToast = (message, isError = false) => {
    setToast({ show: true, message, isError });
    setTimeout(() => setToast({ show: false, message: '', isError: false }), 3000);
  };
  const setLoad = (k, v) => setLoading(p => ({ ...p, [k]: v }));
  const setErr  = (k, v) => setErrors(p  => ({ ...p, [k]: v }));

  // ── fetchers ───────────────────────────────────────────────────────────────
  const loadKPI = useCallback(async () => {
    setLoad('kpi', true); setErr('kpi', null);
    try   { setKpi(await revenueApi.getKPI(startDate, endDate, { process: processFilter, shift: shiftFilter })); }
    catch (e) { setErr('kpi', e.message); }
    finally   { setLoad('kpi', false); }
  }, [startDate, endDate, processFilter, shiftFilter]);

  const loadMonthly = useCallback(async () => {
    setLoad('monthly', true); setErr('monthly', null);
    try   { setMonthly(await revenueApi.getMonthlyPerformance(startDate, endDate)); }
    catch (e) { setErr('monthly', e.message); }
    finally   { setLoad('monthly', false); }
  }, [startDate, endDate]);

  const loadFunnel = useCallback(async () => {
    setLoad('funnel', true); setErr('funnel', null);
    try   { setFunnel(await revenueApi.getFunnel(startDate, endDate)); }
    catch (e) { setErr('funnel', e.message); }
    finally   { setLoad('funnel', false); }
  }, [startDate, endDate]);

  const loadManagers = useCallback(async () => {
    setLoad('managers', true); setErr('managers', null);
    try {
      const data = await revenueApi.getManagersFocus(startDate, endDate);
      setManagers(Array.isArray(data) ? data : data?.managers || data?.data || []);
    } catch (e) { setErr('managers', e.message); }
    finally { setLoad('managers', false); }
  }, [startDate, endDate]);

  const loadTeamInsights = useCallback(async (managerId) => {
    setLoad('insights', true); setErr('insights', null);
    try {
      const data = await revenueApi.getTeamInsights(managerId, startDate, endDate);
      setTeamInsights(data);
      setSelectedMgr(managerId);
    } catch (e) { setErr('insights', e.message); }
    finally { setLoad('insights', false); }
  }, [startDate, endDate]);

  const loadAgents = useCallback(async (tlId) => {
    if (agentsByLead[tlId]) return;
    try {
      const data   = await revenueApi.getTeamLeadAgents(tlId, startDate, endDate);
      const agents = Array.isArray(data) ? data : data?.agents || data?.data || [];
      setAgentsByLead(p => ({ ...p, [tlId]: agents }));
    } catch (e) { showToast(`Agents load failed: ${e.message}`, true); }
  }, [agentsByLead, startDate, endDate]);

  const loadAssignees = useCallback(async () => {
    try {
      const data = await revenueApi.getAssignees(assignment.type);
      const list = Array.isArray(data) ? data : data?.assignees || [];
      setAssignees(list);
      setSelectedAssigneeIds([]);
      const initTargets = {};
      list.forEach(item => {
        const id = item.id ?? item.employeeId ?? item.floorId;
        if (id) {
          initTargets[id] = item.currentTarget ?? item.targetValue ?? item.target ?? '';
        }
      });
      setAssigneeTargets(initTargets);
    } catch (e) { showToast(`Assignees load failed: ${e.message}`, true); }
  }, [assignment.type]);

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadKPI(); loadMonthly(); loadFunnel(); loadManagers();
    setTeamInsights(null); setSelectedMgr(null);
    setExpandedLead(null); setAgentsByLead({});
  }, [startDate, endDate, processFilter, shiftFilter]);

  useEffect(() => {
    if (isAssignModalOpen) loadAssignees();
  }, [isAssignModalOpen, assignment.type]);

  // ── Auto-divide & Assignment Handlers ─────────────────────────────────────
  const handleAssigneeTargetChange = (id, val) => {
    const num = val.replace(/[^0-9]/g, '');
    setAssigneeTargets(prev => ({ ...prev, [id]: num }));
    if (num && Number(num) > 0) {
      setSelectedAssigneeIds(prev => prev.includes(id) ? prev : [...prev, id]);
    }
  };

  const toggleSelectAssignee = (id) => {
    setSelectedAssigneeIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleAutoDivide = () => {
    const total = Number(assignment.target) || 0;
    if (total <= 0) {
      showToast('Please enter a total target amount (e.g. 500000) to auto divide.', true);
      return;
    }
    const effectiveIds = selectedAssigneeIds.length > 0
      ? selectedAssigneeIds
      : assignees.map(a => a.id ?? a.employeeId ?? a.floorId).filter(Boolean);

    if (effectiveIds.length === 0) {
      showToast('No assignees available to divide target.', true);
      return;
    }
    if (selectedAssigneeIds.length === 0) {
      setSelectedAssigneeIds(effectiveIds);
    }
    const share = Math.round(total / effectiveIds.length);
    const newMap = { ...assigneeTargets };
    effectiveIds.forEach(id => {
      newMap[id] = share;
    });
    setAssigneeTargets(newMap);
    showToast(`Divided ₹${total.toLocaleString('en-IN')} equally across ${effectiveIds.length} members (₹${share.toLocaleString('en-IN')} each).`);
  };

  const submitAssignment = async () => {
    const totalTarget = Number(assignment.target) || 0;
    const effectiveIds = selectedAssigneeIds.length > 0
      ? selectedAssigneeIds
      : assignees.map(a => a.id ?? a.employeeId ?? a.floorId).filter(Boolean);

    if (effectiveIds.length === 0) {
      showToast('Please select at least one member or floor.', true);
      return;
    }

    const perAssigneeValues = {};
    let sumAllocated = 0;
    effectiveIds.forEach(id => {
      const numId = typeof id === 'string' && id.startsWith('f') ? Number(id.replace('f', '')) || 1 : Number(id) || id;
      const val = Number(assigneeTargets[id]) || (totalTarget > 0 ? Math.round(totalTarget / effectiveIds.length) : 0);
      perAssigneeValues[String(numId)] = val;
      sumAllocated += val;
    });

    const finalTarget = totalTarget > 0 ? totalTarget : sumAllocated;
    if (finalTarget <= 0) {
      showToast('Please enter a target amount.', true);
      return;
    }

    setAssigning(true);
    try {
      const user = authHelpers.getCurrentUser();
      const assignPromises = effectiveIds.map((id) => {
        const numId = typeof id === 'string' && id.startsWith('f') ? Number(id.replace('f', '')) || 1 : Number(id) || id;
        const individualTarget = Number(assigneeTargets[id] || assigneeTargets[String(id)]) || (totalTarget > 0 ? Math.round(totalTarget / effectiveIds.length) : 0);

        return revenueApi.assignTarget({
          targetType:   'REVENUE',
          assigneeType: assignment.type,
          targetValue:  individualTarget,
          startDate,
          endDate,
          createdById:  user?.id ?? 99,
          assigneeIds:  [numId],
          perAssigneeValues: { [String(numId)]: individualTarget },
        });
      });

      await Promise.all(assignPromises);
      showToast('Revenue target assigned successfully!');
      setIsAssignModalOpen(false);
      setAssignment({ type: 'TEAM_LEAD', target: '' });
      setSelectedAssigneeIds([]);
      setAssigneeTargets({});
      loadManagers();
      if (selectedMgr) loadTeamInsights(selectedMgr);
    } catch (e) {
      showToast(`Assignment failed: ${e.message}`, true);
    } finally {
      setAssigning(false);
    }
  };

  const handleViewAllManagers = async () => {
    try {
      const data = await revenueApi.getAllManagers(startDate, endDate);
      setManagers(Array.isArray(data) ? data : data?.managers || data?.content || []);
    } catch (e) { showToast(`Failed: ${e.message}`, true); }
  };

  const handleExportExcel = async () => {
    showToast('Generating export…');
    try {
      await dashboardApi.exportDashboardData({ dashboardType: 'REVENUE', startDate, endDate });
      showToast('Excel downloaded!');
    } catch {
      const rows = kpi ? [
        { Metric: 'Total Revenue Target',  Value: fmtRs(kpi.totalRevenueTarget ?? kpi.totalRevenueTarget) },
        { Metric: 'Revenue Generated',     Value: fmtRs(kpi.revenueGenerated ?? kpi.revenue) },
        { Metric: 'Verified Revenue',      Value: fmtRs(kpi.verifiedRevenue) },
        { Metric: 'Revenue per Agent',     Value: fmtRs(kpi.revenuePerAgent ?? kpi.avgOrderValue) },
        { Metric: 'Delivered Revenue',     Value: fmtRs(kpi.deliveredRevenue ?? kpi.delivered) },
        { Metric: 'Revenue Growth %',      Value: fmtPct(kpi.revenueGrowth) },
        { Metric: 'Net Revenue %',         Value: fmtPct(kpi.netRevenue ?? kpi.netConversionRate) },
        { Metric: 'Collection Rate %',     Value: fmtPct(kpi.collectionRate ?? kpi.deliveryRate) },
      ] : [{ Metric: 'No data', Value: '' }];
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Revenue');
      XLSX.writeFile(wb, `revenue_target_${startDate}.xlsx`);
      showToast('Excel downloaded (local data).');
    }
  };

  const toggleLeadExpansion = (tlId) => {
    if (expandedLead === tlId) { setExpandedLead(null); return; }
    setExpandedLead(tlId);
    loadAgents(tlId);
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const achieved = monthly?.achieved ?? kpi?.revenueGenerated ?? kpi?.revenue ?? 0;
  const target   = monthly?.target   ?? kpi?.target ?? kpi?.totalTarget ?? 0;
  const pct      = target > 0 ? Math.round((achieved / target) * 100) : 0;
  const gap      = target - achieved;

  const funnelSteps = funnel ? [
    { label: 'Leads',     value: funnel.totalLeads     ?? funnel.leads           ?? 0, color: `${currentTheme.color}60` },
    { label: 'Orders',    value: funnel.orders         ?? funnel.ordersPlaced    ?? 0, color: `${currentTheme.color}80` },
    { label: 'Verified',  value: funnel.verified       ?? funnel.verifiedOrders  ?? 0, color: `${currentTheme.color}A0` },
    { label: 'Delivered', value: funnel.delivered      ?? funnel.deliveredOrders ?? 0, color: currentTheme.color },
  ] : [];

  const Spinner   = () => <Loader2 className="w-4 h-4 animate-spin inline-block mr-1" style={{ color: currentTheme.color }} />;
  const ErrBanner = ({ msg }) => (
    <div className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-2">{msg}</div>
  );

  const tabClass = (tab) =>
    `px-4 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95 ${
      location.pathname === tab
        ? 'text-white shadow-md'
        : (isDark ? 'text-gray-300 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100/50')
    }`;

  const tabStyle = (tab) => location.pathname === tab ? { backgroundColor: currentTheme.color } : {};

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div className={`flex flex-col gap-6 p-4 md:p-6 min-h-screen transition-colors duration-300 ${!isDark ? 'bg-[#F8FAFC]' : ''}`} style={isDark ? { background: currentTheme.cssGradient } : {}}>

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-gray-800">Business Dashboard</h1>
        <div className="flex items-center gap-4 flex-wrap">
          <div className={`custom-range-picker rounded-lg overflow-hidden border ${isDark ? 'border-white/10 bg-black/20' : 'border-gray-200 bg-white'}`}>
            <RangePicker
              value={dates}
              onChange={(vals) => { setDates(vals); setAgentsByLead({}); }}
              format="MMM DD, YYYY"
              allowClear={false}
              suffixIcon={<ChevronDown className={`w-4 h-4 ${isDark ? 'text-gray-300' : 'text-gray-400'}`} />}
              style={{ borderRadius: '8px', border: 'none', fontWeight: 'bold', background: 'transparent' }}
              className={isDark ? 'dark-range-picker' : ''}
            />
          </div>
          <div className={`flex items-center p-1 rounded-xl gap-1 ${isDark ? 'bg-black/40' : 'bg-[#F1F5F9]'}`}>
            <button className={tabClass('/mis-reports/business-dashboard')} style={tabStyle('/mis-reports/business-dashboard')} onClick={() => navigate('/mis-reports/business-dashboard')}>Orders target</button>
            <button className={tabClass('/mis-reports/revenue-target')}    style={tabStyle('/mis-reports/revenue-target')} onClick={() => navigate('/mis-reports/revenue-target')}>Revenue target</button>
            <button className={tabClass('/mis-reports/conversion-target')} style={tabStyle('/mis-reports/conversion-target')} onClick={() => navigate('/mis-reports/conversion-target')}>Conversion target</button>
          </div>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className={`flex flex-wrap items-center justify-between gap-4 p-4 rounded-xl shadow-sm border ${isDark ? 'bg-black/20 border-white/10' : 'bg-white border-gray-100'}`}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5 tracking-wider">Search Employee</span>
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                placeholder="ID, name or code…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 w-56 transition-all ${isDark ? 'bg-black/20 border-white/10 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-200'}`}
                style={{ ringColor: currentTheme.color + '20', borderColor: currentTheme.color + '40' }}
              />
            </div>
          </div>

          {[
            { label: 'Process', value: processFilter, setter: setProcessFilter, options: [{ v: '', l: 'All processes' }, { v: 'FLead', l: 'FLead' }, { v: 'RTO', l: 'RTO' }] },
            { label: 'Shift',   value: shiftFilter,   setter: setShiftFilter,   options: [{ v: '', l: 'All shifts' }, { v: 'MORNING', l: 'Morning' }, { v: 'EVENING', l: 'Evening' }, { v: 'NIGHT', l: 'Night' }] },
          ].map(({ label, value, setter, options }) => (
            <div key={label} className="flex flex-col">
              <span className="text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">{label}</span>
              <div className="relative">
                <select value={value} onChange={(e) => setter(e.target.value)}
                  className={`appearance-none border rounded-lg px-3 py-2 pr-8 text-sm font-semibold min-w-[140px] focus:outline-none focus:ring-2 cursor-pointer ${isDark ? 'bg-black/20 border-white/10 text-white hover:bg-white/10 [&>option]:bg-[#12141a]' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'}`}
                  style={{ ringColor: currentTheme.color + '20' }}
                >
                  {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsAssignModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white shadow-md transition-all active:scale-95 cursor-pointer"
            style={{ backgroundColor: currentTheme.color }}
          >
            <Plus className="w-4 h-4" /> Assign Target
          </button>
          <ExcelExportButton onClick={handleExportExcel} isDownloading={loading.export} />
          <button onClick={() => { showToast('Generating PDF…'); setTimeout(() => window.print(), 500); }} className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-bold transition-all font-black ${isDark ? 'bg-black/20 border-white/10 text-gray-300 hover:bg-white/10' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {/* ── KPI Row 1 ── */}
      {errors.kpi && <ErrBanner msg={errors.kpi} />}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total Revenue Target',  value: fmtRs(kpi?.totalRevenueTarget ?? kpi?.totalRevenueTarget),                          trend: kpi?.targetTrend,    sub: `${startDate} – ${endDate}` },
          { label: 'Revenue Generated',     value: fmtRs(kpi?.revenueGenerated ?? kpi?.revenue ?? kpi?.ordersPlaced), trend: kpi?.revenueTrend,   sub: kpi?.revenueAchievementPct != null ? `${fmtPct(kpi.revenueAchievementPct)} achieved` : null },
          { label: 'Verified Revenue',      value: fmtRs(kpi?.verifiedRevenue ?? kpi?.verifiedOrders),               trend: kpi?.verifiedTrend,  sub: kpi?.verificationRate != null ? `${fmtPct(kpi.verificationRate)} verification rate` : null, subColor: 'text-green-600' },
          { label: 'Revenue per Agent',     value: fmtRs(kpi?.revenuePerAgent ?? kpi?.avgOrderValue),                trend: kpi?.aovTrend,       sub: 'Average', subColor: 'text-orange-500' },
          { label: 'Delivered Revenue',     value: fmtRs(kpi?.deliveredRevenue ?? kpi?.deliveredOrders ?? kpi?.delivered), trend: kpi?.deliveryTrend, sub: kpi?.deliveryRate != null ? `${fmtPct(kpi.deliveryRate)} delivery rate` : null, subColor: 'text-green-600' },
        ].map((card, i) => (
          <div key={i} className={`p-5 rounded-2xl border shadow-sm hover:shadow-md transition-shadow ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-gray-100'}`}>
            <div className="flex justify-between items-start mb-2">
              <span className="text-sm font-bold text-gray-500 max-w-[120px] leading-tight">{card.label}</span>
              {card.trend != null && (
                <div className="flex items-center gap-1 font-bold text-xs text-green-500">
                  <TrendingUp className="w-3 h-3" />
                  {typeof card.trend === 'number' ? fmtPct(card.trend) : card.trend}
                </div>
              )}
            </div>
            <div className="text-3xl font-black text-gray-800 mb-1">
              {loading.kpi ? <Spinner /> : (card.value ?? '—')}
            </div>
            {card.sub && <p className="text-xs font-medium"><span className={card.subColor ?? 'text-gray-400'}>{card.sub}</span></p>}
          </div>
        ))}
      </div>

      {/* ── KPI Row 2 ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Revenue Growth %',  value: fmtPct(kpi?.revenueGrowth ?? kpi?.grossConversionPct),    raw: kpi?.revenueGrowth   ?? 0, color: currentTheme.color },
          { label: 'Net Revenue %',     value: fmtPct(kpi?.netRevenue    ?? kpi?.netConversionRate),     raw: kpi?.netRevenue      ?? 0, color: '#22c55e' },
          { label: 'Collection Rate',   value: fmtPct(kpi?.collectionRate ?? kpi?.deliveryRate),         raw: kpi?.collectionRate  ?? 0, sub: kpi?.collectionTarget != null ? `Target: ${fmtPct(kpi.collectionTarget)}` : null, subColor: 'text-green-600', color: '#16a34a' },
          { label: 'Avg Deal Size',     value: fmtRs(kpi?.avgDealSize ?? kpi?.avgOrderValue),            raw: 65, sub: kpi?.dealSizeDelta != null ? `+${fmtN(kpi.dealSizeDelta)} vs last month` : null, color: '#94a3b8' },
        ].map((card, i) => (
          <div key={i} className={`p-5 rounded-2xl border shadow-sm ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-gray-100'}`}>
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{card.label}</span>
            <div className="text-2xl font-black text-gray-800 my-1">{loading.kpi ? <Spinner /> : (card.value ?? '—')}</div>
            {card.sub && <p className={`text-[10px] font-bold ${card.subColor ?? 'text-gray-400'}`}>{card.sub}</p>}
            <div className="w-full h-1.5 bg-gray-100 rounded-full mt-3 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(100, Math.max(0, card.raw ?? 0))}%`, backgroundColor: card.color }} />
            </div>
          </div>
        ))}
      </div>

      {/* ── Monthly Performance + Funnel ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Achievement card */}
        <div className={`lg:col-span-2 p-6 rounded-3xl border shadow-sm flex flex-col md:flex-row items-center gap-8 ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-gray-100'}`}>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-xs font-bold uppercase mb-4 tracking-widest" style={{ color: currentTheme.color }}>
              <div className="w-5 h-5 rounded flex items-center justify-center" style={{ backgroundColor: `${currentTheme.color}20` }}>
                <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: currentTheme.color }} />
              </div>
              Monthly Revenue Performance
            </div>
            {loading.monthly ? (
              <div className="py-8 text-center text-gray-400 text-sm"><Spinner />Loading…</div>
            ) : errors.monthly ? (
              <ErrBanner msg={errors.monthly} />
            ) : (
              <>
                <div className="flex items-baseline gap-2 mb-6">
                  <h2 className="text-6xl font-black tracking-tighter" style={{ color: currentTheme.color }}>{fmtRs(achieved)}</h2>
                  <span className="text-2xl font-bold text-gray-400">/ {fmtRs(target)} target</span>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  {gap > 0 && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-bold border ${isDark ? 'bg-black/40 text-orange-400 border-orange-500/20' : 'bg-orange-50 text-orange-600 border-orange-100'}`}>
                      <AlertCircle className="w-4 h-4" />
                      {fmtRs(gap)} Revenue Shortfall
                    </div>
                  )}
                  {(monthly?.daysRemaining ?? kpi?.daysRemaining) != null && (
                    <p className="text-xs text-gray-400 font-bold">
                      Goal deadline: {monthly?.daysRemaining ?? kpi?.daysRemaining} days remaining
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Gauge */}
          <div className="relative flex flex-col items-center flex-shrink-0">
            <div className="relative flex flex-col items-center justify-center rounded-[40px] p-10 min-w-[200px]" style={{ backgroundColor: `${currentTheme.color}10` }}>
              <span className="text-6xl font-black leading-none" style={{ color: currentTheme.color }}>{pct}%</span>
              <div className="w-16 h-1 mx-auto my-3 rounded-full opacity-30" style={{ backgroundColor: currentTheme.color }} />
              <p className="text-xs font-black uppercase tracking-[0.2em]" style={{ color: currentTheme.color }}>Achievement</p>
            </div>
          </div>
        </div>

        {/* Funnel */}
        <div className={`p-6 rounded-3xl border shadow-sm min-h-[320px] ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-gray-100'}`}>
          <h3 className={`text-sm font-bold mb-6 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{dates?.[0]?.format('MMMM')} sales funnel</h3>
          {loading.funnel ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm"><Spinner />Loading…</div>
          ) : errors.funnel ? (
            <ErrBanner msg={errors.funnel} />
          ) : funnelSteps.length === 0 ? (
            <p className="text-sm text-gray-400 text-center mt-8">No funnel data</p>
          ) : (
            <>
              <div className="flex flex-col items-center gap-1">
                {funnelSteps.map((step, i) => {
                  const topW = 280 - i * 44;
                  const botW = 280 - (i + 1) * 44;
                  const svgW = 300;
                  const tX   = (svgW - topW) / 2;
                  const bX   = (svgW - botW) / 2;
                  const pctLbl = funnelSteps[0].value > 0 ? `${((step.value / funnelSteps[0].value) * 100).toFixed(1)}%` : '0%';
                  const path = `M ${tX} 0 L ${tX + topW} 0 Q ${tX + topW - 6} 22 ${bX + botW} 44 L ${bX} 44 Q ${tX + 6} 22 ${tX} 0 Z`;
                  return (
                    <div key={step.label} className="relative" style={{ width: svgW, height: 44 }}>
                      <svg width={svgW} height={44} viewBox={`0 0 ${svgW} 44`} style={{ display: 'block' }}>
                        <path d={path} fill={step.color} />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-white text-[11px] font-black tracking-wide pointer-events-none">
                        {step.label}: {fmtN(step.value)} ({pctLbl})
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8 pt-5 border-t border-gray-50 flex justify-around">
                <div className="text-center">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase mb-1 tracking-widest">Conversion</p>
                  <p className="text-lg font-black text-gray-800">{fmtPct(funnel?.conversionRate ?? funnel?.conversion)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase mb-1 tracking-widest">Efficiency</p>
                  <p className="text-lg font-black" style={{ color: currentTheme.color }}>{fmtPct(funnel?.efficiency ?? funnel?.efficiencyRate)}</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Managers Revenue Focus ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-black text-gray-800">Managers Revenue Focus</h2>
          <button onClick={handleViewAllManagers} className="flex items-center text-sm font-bold hover:opacity-80" style={{ color: currentTheme.color }}>
            View all managers <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
        {errors.managers && <ErrBanner msg={errors.managers} />}
        {loading.managers ? (
          <div className="text-center py-10 text-gray-400"><Spinner />Loading managers…</div>
        ) : managers.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No manager data available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {managers
              .filter((m) => {
                if (!searchQuery) return true;
                const q = searchQuery.toLowerCase();
                return m.managerName?.toLowerCase().includes(q) || String(m.managerId ?? m.id ?? '').includes(q);
              })
              .map((manager) => {
                const mId  = manager.id ?? manager.managerId;
                const mPct = manager.target > 0 ? Math.round((manager.achieved / manager.target) * 100) : 0;
                const sc   = statusConfig(mPct);
                return (
                  <div key={mId} className={`p-6 rounded-2xl border shadow-sm hover:shadow-lg transition-all ${isDark ? 'bg-black/20 text-white' : 'bg-white'} ${selectedMgr === mId ? (isDark ? 'border-blue-500/50' : 'border-blue-200') : (isDark ? 'border-white/10' : 'border-gray-100')}`}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 bg-gray-200 rounded-full border-2 border-white shadow-sm overflow-hidden flex-shrink-0">
                        <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${manager.managerName}`} alt={manager.managerName} />
                      </div>
                      <div>
                        <h4 className="text-base font-black text-gray-900 leading-none mb-1">{manager.managerName}</h4>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{manager.role ?? manager.designation ?? 'Manager'}</p>
                      </div>
                    </div>
                    <div className="flex justify-between mb-6">
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Target</p>
                        <p className="text-xl font-black text-gray-800">{fmtRs(manager.target)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Achieved</p>
                        <p className="text-xl font-black text-gray-800">{fmtRs(manager.achieved)}</p>
                      </div>
                    </div>
                    <div className="mb-6">
                      <div className="flex justify-between items-end mb-2">
                        <span className="text-[12px] font-black" style={{ color: sc.color }}>{mPct}% achievement</span>
                        <span className="text-[10px] font-bold text-gray-400">{sc.label}</span>
                      </div>
                      <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${mPct}%`, backgroundColor: sc.color }} />
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
                          <BarChart3 className="w-4 h-4 text-green-500" />
                        </div>
                        <div>
                          <p className="text-xs font-black text-gray-800 leading-none">View team insights</p>
                          <p className="text-[10px] text-gray-400 mt-1 font-medium">
                            {manager.teamLeadCount ?? manager.leads ?? '—'} TLs • {manager.agentCount ?? manager.agents ?? '—'} agents
                          </p>
                        </div>
                      </div>
                      <button onClick={() => loadTeamInsights(mId)} className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-50 hover:bg-gray-100'}`}>
                        {loading.insights && selectedMgr === mId
                          ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: currentTheme.color }} />
                          : <ChevronDown className="w-4 h-4 text-gray-400" />
                        }
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* ── Team Insights ── */}
      {(teamInsights || loading.insights) && (
        <div className={`p-6 rounded-[32px] border shadow-sm relative overflow-hidden ${isDark ? 'bg-black/20 text-white' : 'bg-white'}`} style={{ borderColor: `${currentTheme.color}20` }}>
          <div className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(to right, ${currentTheme.color}10, ${currentTheme.color}30, ${currentTheme.color}10)` }} />
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8 bg-[#F8FAFC] p-5 rounded-2xl border border-gray-50">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm" style={{ backgroundColor: `${currentTheme.color}10` }}>
                <Users className="w-6 h-6" style={{ color: currentTheme.color }} />
              </div>
              <div>
                <h2 className="text-lg font-black text-gray-800">
                  {teamInsights?.managerName ? `Team insights: ${teamInsights.managerName}'s group` : 'Team insights'}
                </h2>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                  {teamInsights?.teamLeadCount ?? '—'} team leads • {teamInsights?.agentCount ?? '—'} agents
                </p>
              </div>
            </div>
            <button onClick={() => setIsAssignModalOpen(true)} className={`border px-5 py-2.5 rounded-xl text-xs font-black shadow-sm transition-all active:scale-95 ${isDark ? 'bg-black/20 border-white/10 text-gray-300 hover:bg-white/10' : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'}`}>
              Manage assignment
            </button>
          </div>

          {errors.insights && <ErrBanner msg={errors.insights} />}

          {loading.insights ? (
            <div className="text-center py-8 text-gray-400"><Spinner />Loading team insights…</div>
          ) : (
            <div className="space-y-4">
              {(teamInsights?.teamLeads ?? teamInsights?.teamLead ?? []).map((lead) => {
                const tlId   = lead.id ?? lead.employeeId;
                const tlPct  = lead.target > 0 ? Math.round((lead.achieved / lead.target) * 100) : 0;
                const sc     = statusConfig(tlPct);
                const isExp  = expandedLead === tlId;
                const agents = agentsByLead[tlId] ?? lead.agents ?? [];
                const tlGap  = lead.gap ?? (lead.target - lead.achieved);

                return (
                  <div key={tlId} className={`rounded-2xl border overflow-hidden shadow-sm transition-all duration-300 ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-gray-100'}`}>
                    <div
                      onClick={() => toggleLeadExpansion(tlId)}
                      className={`flex flex-wrap items-center justify-between gap-4 p-4 cursor-pointer transition-all group ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50/50'} ${isExp ? (isDark ? 'bg-white/10' : 'bg-[#F8FAFC]') : ''}`}
                    >
                      <div className="flex items-center gap-6 min-w-[280px]">
                        <ChevronRight className={`w-4 h-4 text-gray-300 transition-transform duration-300 ${isExp ? 'rotate-90' : ''}`} style={isExp ? { color: currentTheme.color } : {}} />
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-gray-200 border-2 border-white shadow-sm overflow-hidden flex-shrink-0">
                            <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${lead.name}`} alt={lead.name} />
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">Team lead</p>
                            <p className="text-sm font-black text-gray-800">{lead.name}</p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-12 flex-1 max-w-2xl px-6">
                        {[
                          { lbl: 'Target',         val: fmtRs(lead.target),                                 color: 'text-gray-800' },
                          { lbl: 'Achieved',        val: fmtRs(lead.achieved),                               color: 'text-gray-800' },
                          { lbl: 'Avg. deal size',  val: fmtRs(lead.avgDealSize ?? lead.avgOrderValue ?? lead.aov), color: 'text-orange-500' },
                          { lbl: 'Gap',             val: fmtRs(Math.abs(tlGap)),                             color: tlGap < 0 ? 'text-red-500' : 'text-green-500' },
                        ].map((s) => (
                          <div key={s.lbl}>
                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">{s.lbl}</p>
                            <p className={`text-base font-black ${s.color}`}>{s.val}</p>
                          </div>
                        ))}
                      </div>

                      <div className="min-w-[150px] text-right">
                        <span className="px-4 py-2 rounded-xl text-[9px] font-black border tracking-wider"
                          style={{ color: sc.color, backgroundColor: sc.bg, borderColor: sc.border }}>
                          {sc.label.toUpperCase()} ({tlPct}%)
                        </span>
                      </div>
                    </div>

                    {/* Agents sub-table */}
                    {isExp && (
                      <div className="bg-[#FBFCFE] border-t border-gray-100 p-6">
                        <div className="grid grid-cols-7 gap-4 px-6 mb-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                          <div className="col-span-2 ml-10">Agent name</div>
                          <div>Target</div>
                          <div>Revenue</div>
                          <div>Deal size</div>
                          <div>Growth %</div>
                          <div>Collection %</div>
                        </div>
                        {agents.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-4">No agent data</p>
                        ) : (
                          <div className="space-y-2">
                            {agents.map((agent, j) => {
                              const growth = agent.revenueGrowth ?? agent.conversionRate ?? agent.conversion ?? 0;
                              const coll   = agent.collectionRate ?? agent.deliveryRate ?? agent.delivery ?? 0;
                              const aColor = growth >= 15 ? '#22c55e' : growth >= 10 ? '#3b82f6' : '#ef4444';
                              const cBg    = coll >= 90 ? '#f0fdf4' : '#fff7ed';
                              const cClr   = coll >= 90 ? '#16a34a' : '#f97316';
                              return (
                                <div key={j} className="flex items-center h-14 bg-white rounded-xl border border-gray-50 shadow-sm relative overflow-hidden"
                                  style={{ borderLeft: `3px solid ${aColor}` }}>
                                  <div className="grid grid-cols-7 gap-4 flex-1 items-center px-6">
                                    <div className="col-span-2 flex items-center gap-3 ml-4">
                                      <div className="w-8 h-8 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
                                        <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${agent.name}`} alt={agent.name} />
                                      </div>
                                      <span className="text-xs font-black text-gray-700 truncate">{agent.name}</span>
                                    </div>
                                    <div className="text-xs font-bold text-gray-500">{fmtRs(agent.target)}</div>
                                    <div className="text-xs font-black text-gray-800">{fmtRs(agent.revenue ?? agent.revenueGenerated ?? agent.ordersPlaced)}</div>
                                    <div className="text-xs font-black text-orange-500">{fmtRs(agent.avgDealSize ?? agent.avgOrderValue ?? agent.aov)}</div>
                                    <div className="flex items-center gap-3">
                                      <span className="text-[10px] font-black" style={{ color: aColor }}>{fmtPct(growth)}</span>
                                      <div className="w-16 h-1 bg-gray-100 rounded-full overflow-hidden">
                                        <div className="h-full rounded-full" style={{ width: `${Math.min(100, growth)}%`, backgroundColor: aColor }} />
                                      </div>
                                    </div>
                                    <div>
                                      <span className="px-2 py-1 rounded-md text-[9px] font-black" style={{ color: cClr, backgroundColor: cBg }}>
                                        {fmtPct(coll)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div className="mt-4 flex items-center justify-between px-6 border-t border-gray-50 pt-4">
                          <p className="text-xs text-gray-400 font-bold">Showing {agents.length} agents</p>
                          <button onClick={() => navigate(`/mis-reports/teamleads/${tlId}/agents`)} className="flex items-center gap-1 text-xs font-black hover:opacity-80" style={{ color: currentTheme.color }}>
                            View all agent list <ChevronRight className="w-4 h-4 mt-0.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Target Assignment Modal ── */}
      {isAssignModalOpen && (() => {
        const targetGoal = Number(assignment.target) || 0;
        const effectiveSelectedIds = selectedAssigneeIds.length > 0
          ? selectedAssigneeIds
          : assignees.map(a => a.id ?? a.employeeId ?? a.floorId).filter(Boolean);

        const selectedTotal = effectiveSelectedIds.reduce((sum, id) => sum + (Number(assigneeTargets[id]) || 0), 0);
        const isOverAllocated = targetGoal > 0 && selectedTotal > targetGoal;
        const isExactAllocated = targetGoal > 0 && selectedTotal === targetGoal;
        const remainingBudget = Math.max(0, targetGoal - selectedTotal);

        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-gray-950/70 backdrop-blur-md overflow-y-auto">
            <div className="bg-white w-full max-w-2xl rounded-[32px] shadow-2xl border border-white/20 overflow-hidden my-auto max-h-[90vh] flex flex-col">
              
              {/* Header */}
              <div className="p-6 sm:p-8 pb-4 border-b border-gray-100 flex items-start justify-between flex-shrink-0">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-xs" style={{ backgroundColor: `${currentTheme.color}15`, color: currentTheme.color }}>
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">Assign Revenue Target</h3>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mt-1">Monthly revenue budget & auto divide</p>
                  </div>
                </div>
                <button onClick={() => setIsAssignModalOpen(false)} className={`w-10 h-10 flex items-center justify-center rounded-2xl transition-all ${isDark ? 'bg-white/10 text-gray-400 hover:bg-red-500/20 hover:text-red-400' : 'bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-500'}`}>
                  <AlertCircle className="w-5 h-5 rotate-45" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="p-6 sm:p-8 overflow-y-auto space-y-6 flex-1 custom-scrollbar">
                {/* Level toggle */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4" style={{ color: currentTheme.color }} />
                    <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Select assignment level</label>
                  </div>
                  <div className={`flex p-1.5 rounded-2xl border ${isDark ? 'bg-black/40 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                    {[
                      { label: 'Team lead', val: 'TEAM_LEAD' },
                      { label: 'Floor', val: 'FLOOR' },
                      { label: 'Individual agent', val: 'EMPLOYEE' },
                    ].map((opt) => (
                      <button
                        key={opt.val}
                        type="button"
                        onClick={() => setAssignment(a => ({ ...a, type: opt.val }))}
                        className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-black transition-all duration-200 cursor-pointer ${
                          assignment.type === opt.val
                            ? 'bg-white shadow-md text-gray-900 border border-gray-100'
                            : 'text-gray-500 hover:text-gray-800'
                        }`}
                        style={assignment.type === opt.val ? { color: currentTheme.color } : {}}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Target value & Auto Divide */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-emerald-600" />
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Total Monthly Revenue Target (₹)</label>
                    </div>
                  </div>
                  <div className="relative group">
                    <input
                      type="number"
                      placeholder="e.g. 1000000"
                      value={assignment.target}
                      onChange={(e) => setAssignment(a => ({ ...a, target: e.target.value }))}
                      className={`w-full border rounded-2xl px-5 py-3.5 text-base font-black placeholder:text-gray-300 focus:outline-none focus:ring-4 transition-all pr-20 ${
                        isDark ? 'bg-black/40 border-white/10 text-white focus:ring-emerald-500/20' : 'bg-gray-50 border-gray-200 text-gray-800 focus:ring-emerald-500/10 focus:bg-white'
                      }`}
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-60 group-focus-within:opacity-100 transition-opacity">
                      <span className="text-xs font-black text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md border border-emerald-200">₹ INR</span>
                    </div>
                  </div>
                </div>

                {/* Assignees Header with Select All & Auto Divide */}
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-gray-100">
                    <div>
                      <span className="text-xs font-black text-gray-800 uppercase tracking-wider">
                        {assignment.type === 'TEAM_LEAD' ? 'Team Leaders' : assignment.type === 'FLOOR' ? 'Floors' : 'Agents'} ({assignees.length})
                      </span>
                      <span className="text-[11px] text-gray-400 ml-2">
                        {selectedAssigneeIds.length > 0 ? selectedAssigneeIds.length : assignees.length} selected
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedAssigneeIds.length === assignees.length && assignees.length > 0) {
                            setSelectedAssigneeIds([]);
                          } else {
                            setSelectedAssigneeIds(assignees.map(a => a.id ?? a.employeeId ?? a.floorId).filter(Boolean));
                          }
                        }}
                        className="px-3 py-1 rounded-xl border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-50 transition-all cursor-pointer shadow-2xs"
                      >
                        {selectedAssigneeIds.length === assignees.length && assignees.length > 0 ? 'Deselect All' : 'Select All'}
                      </button>

                      <button
                        type="button"
                        onClick={handleAutoDivide}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 text-[#065f46] border border-[#a7f3d0] text-xs font-black transition-all shadow-2xs cursor-pointer active:scale-95"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                        Auto Divide
                      </button>
                    </div>
                  </div>

                  {/* Assignees List */}
                  {assignees.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-6">No {assignment.type === 'TEAM_LEAD' ? 'team leads' : assignment.type === 'FLOOR' ? 'floors' : 'agents'} available.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-60 overflow-y-auto pr-1">
                      {assignees.map((item) => {
                        const id = item.id ?? item.employeeId ?? item.floorId;
                        const isSelected = selectedAssigneeIds.includes(id);

                        return (
                          <div
                            key={id}
                            onClick={() => toggleSelectAssignee(id)}
                            className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer select-none ${
                              isSelected ? 'border-emerald-500 bg-emerald-50/30 shadow-xs' : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              {isSelected ? (
                                <div className="w-4 h-4 rounded-full bg-emerald-600 flex items-center justify-center text-white flex-shrink-0">
                                  <Check className="w-3 h-3 stroke-[3]" />
                                </div>
                              ) : (
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex items-center justify-center flex-shrink-0 bg-white" />
                              )}
                              <div className="min-w-0">
                                <p className="text-xs font-bold text-gray-900 truncate leading-tight">{item.name || `Member ${id}`}</p>
                                <p className="text-[10px] text-gray-400 truncate">{item.teamName || item.role || 'Member'}</p>
                              </div>
                            </div>

                            <div className="pl-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                              <div className="border border-gray-300 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/20 rounded-lg px-2 py-0.5 min-w-[70px] max-w-[95px] bg-white text-right">
                                <input
                                  type="text"
                                  placeholder="₹0"
                                  value={assigneeTargets[id] !== undefined ? assigneeTargets[id] : ''}
                                  onChange={(e) => handleAssigneeTargetChange(id, e.target.value)}
                                  className="w-full text-right text-xs font-black text-gray-900 outline-none bg-transparent"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ── Live Remaining & Allocation Counters ── */}
                {targetGoal > 0 && (
                  <div className="space-y-2.5 pt-2 border-t border-gray-100">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div className="p-2.5 rounded-xl border border-gray-200 bg-slate-50 flex items-center justify-between">
                        <span className="text-[11px] font-bold text-gray-500">🎯 Total Target Goal</span>
                        <span className="text-xs font-black text-gray-900 font-mono">₹{targetGoal.toLocaleString('en-IN')}</span>
                      </div>

                      <div className={`p-2.5 rounded-xl border flex items-center justify-between ${
                        isOverAllocated ? 'border-amber-300 bg-amber-50 text-amber-950' : 'border-blue-200 bg-blue-50 text-blue-950'
                      }`}>
                        <span className="text-[11px] font-bold">✅ Allocated</span>
                        <span className="text-xs font-black font-mono">₹{selectedTotal.toLocaleString('en-IN')}</span>
                      </div>

                      <div className={`p-2.5 rounded-xl border flex items-center justify-between ${
                        isOverAllocated
                          ? 'border-red-300 bg-red-50 text-red-700'
                          : isExactAllocated
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                          : 'border-emerald-200 bg-emerald-50 text-emerald-900'
                      }`}>
                        <span className="text-[11px] font-bold">⏳ Bacha Hua</span>
                        <span className="text-xs font-black font-mono">
                          {isOverAllocated ? `-₹${(selectedTotal - targetGoal).toLocaleString('en-IN')}` : `₹${remainingBudget.toLocaleString('en-IN')}`}
                        </span>
                      </div>
                    </div>

                    {/* Status Message */}
                    <div className={`p-2.5 rounded-xl border flex items-center gap-2 text-xs ${
                      isOverAllocated
                        ? 'bg-amber-50 border-amber-300 text-amber-900'
                        : isExactAllocated
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                        : 'bg-slate-50 border-gray-200 text-gray-700'
                    }`}>
                      {isOverAllocated ? (
                        <>
                          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                          <span>⚠️ <strong>Warning:</strong> Total allocated (₹{selectedTotal.toLocaleString('en-IN')}) exceeds budget goal by ₹{(selectedTotal - targetGoal).toLocaleString('en-IN')}!</span>
                        </>
                      ) : isExactAllocated ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          <span>✓ <strong>Balanced:</strong> Total distributed matches target goal exactly (100%).</span>
                        </>
                      ) : (
                        <>
                          <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <span>ℹ️ ₹{remainingBudget.toLocaleString('en-IN')} remaining to be distributed.</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-100 flex items-center gap-4 bg-gray-50/50 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setIsAssignModalOpen(false)}
                  className="flex-1 py-3 px-5 rounded-2xl text-xs font-black text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-all tracking-wider cursor-pointer"
                >
                  DISCARD
                </button>
                <button
                  type="button"
                  onClick={submitAssignment}
                  disabled={assigning}
                  className="flex-[2] bg-gradient-to-br from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 disabled:opacity-60 text-white px-8 py-3.5 rounded-2xl text-xs font-black shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {assigning && <Loader2 className="w-4 h-4 animate-spin" />}
                  APPLY REVENUE TARGET
                  {!assigning && <ChevronRight className="w-4 h-4" />}
                </button>
              </div>

            </div>
          </div>
        );
      })()}

      {/* ── Toast ── */}
      {toast.show && (
        <div className="fixed top-10 left-1/2 -translate-x-1/2 z-[200] animate-in slide-in-from-top-10 fade-in duration-500">
          <div className={`px-8 py-4 rounded-[24px] shadow-2xl flex items-center gap-4 ${toast.isError ? 'bg-red-600' : 'bg-gray-900/90 backdrop-blur-xl border border-white/10'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${toast.isError ? 'bg-red-400' : 'bg-green-500'} shadow-lg`}>
              {toast.isError ? <AlertCircle className="w-4 h-4 text-white" /> : <TrendingUp className="w-4 h-4 text-white" />}
            </div>
            <p className="text-sm font-black text-white tracking-wide">{toast.message}</p>
          </div>
        </div>
      )}

    </div>
  );
};

export default MISRevenueTarget;